import type { FunctionComponent, HTMLProps, KeyboardEvent } from 'react'
import React, { useEffect } from 'react'

import classnames from 'classnames'

import { InfoIcon } from '../../Icons/Miscellaneous/Info'
import { useTooltip } from '../hooks/useTooltip'
import { Tooltip } from '../Tooltip'
import styles from './TooltipWithButton.module.scss'

export const TooltipWithButton: FunctionComponent<TooltipWithButtonProps> = ({
  contents,
  title,
  ariaLabel,
  className,
  tooltipClassName,
  openHandler,
  closeHandler,
  isTooltipDisplayedAboveOverride,
  tooltipAlignmentOverride,
  ...rest
}) => {
  const {
    isTooltipShown,
    isTooltipDisplayedAbove,
    tooltipAlignment,
    tooltipRef,
    tooltipButtonRef,
    tooltipCloseButtonRef,
    handleTooltipClick,
    handleTooltipKeyDown,
    clickOutsideHandler,
  } = useTooltip(
    false,
    isTooltipDisplayedAboveOverride,
    tooltipAlignmentOverride
  )

  const clickHandler = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (openHandler && !isTooltipShown) {
      openHandler()
    }

    if (closeHandler && isTooltipShown) {
      closeHandler()
    }

    handleTooltipClick(e)
  }

  const handleClickOutside = (e: MouseEvent | TouchEvent) => {
    if (
      !tooltipButtonRef?.current?.contains(e.target as Node) &&
      !tooltipRef?.current?.contains(e.target as Node)
    ) {
      clickOutsideHandler()

      if (closeHandler) {
        closeHandler()
      }
    }
  }

  const keyDownHandler = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') {
      e.preventDefault()
      e.stopPropagation()
    }

    if (e.key === 'Enter' || e.code === 'Space') {
      if (openHandler && !isTooltipShown) {
        openHandler()
      }

      if (closeHandler && isTooltipShown) {
        closeHandler()
      }

      handleTooltipKeyDown(e)
    }
  }

  useEffect(() => {
    if (closeHandler && isTooltipShown) {
      document.addEventListener('wheel', closeHandler)
    }

    return () => {
      if (closeHandler) {
        document.removeEventListener('wheel', closeHandler)
      }
    }
  }, [closeHandler, isTooltipShown])

  return (
    <span
      data-testid="main-container"
      className={classnames(
        styles.mainContainer,
        isTooltipDisplayedAbove && styles.tooltipDisplayedAbove,
        className
      )}
      {...rest}
    >
      <button
        type="button"
        data-testid="info-button"
        onClick={clickHandler}
        onKeyDown={keyDownHandler}
        className={classnames(
          styles.infoContainer,
          isTooltipShown && styles.active
        )}
        aria-label={`More about ${title || ariaLabel}`}
        ref={tooltipButtonRef as React.RefObject<HTMLButtonElement>}
        aria-expanded={isTooltipShown}
        aria-controls="tooltip"
      >
        <InfoIcon data-testid="info-icon" className={styles.info} />
      </button>
      <Tooltip
        contents={contents}
        title={title}
        tooltipClassName={tooltipClassName}
        isTooltipOpen={isTooltipShown}
        isTooltipDisplayedAbove={isTooltipDisplayedAbove}
        tooltipAlignment={tooltipAlignment}
        tooltipRef={tooltipRef}
        tooltipCloseButtonRef={tooltipCloseButtonRef}
        handleTooltipClick={handleTooltipClick}
        handleTooltipKeyDown={handleTooltipKeyDown}
        handleClickOutside={handleClickOutside}
        closeHandler={closeHandler}
      />
    </span>
  )
}

///////// IMPLEMENTATION /////////

export type TooltipWithButtonProps = {
  contents: string
  ariaLabel: string
  title?: string
  className?: string
  tooltipClassName?: string
  openHandler?: () => void
  closeHandler?: () => void
  isTooltipDisplayedAboveOverride?: boolean
  tooltipAlignmentOverride?: string
} & HTMLProps<HTMLSpanElement>
